import { FC, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { CommunicationMessageEvents } from '../interfaces/message-events';
import styles from './SetupGuideDocs.module.scss';
import { DocsIframe } from '../DocsIframe';
import { DocWindowMessage, SetupGuideTracking } from './interfaces/setup-guide-tracking';
import { useDocs } from './hooks/useDocs';
import { getDataIdGenerator } from '../../shared/utils/dataId';
import { useAnalyticsTracker } from '../../tracking/hooks/useAnalyticsTracker';

export interface SetupGuideDocsProps {
  dataId: string;
  className?: string;
  iframeClassName?: string;
  areTabsRendered?: boolean;
  isParentLoading?: boolean;
  docLink: string;
  queryParams?: Record<string, string>;
  LoadingComponent?: FC;
}

export function SetupGuideDocs({
  dataId,
  className = '',
  iframeClassName = '',
  areTabsRendered,
  isParentLoading,
  docLink,
  queryParams,
  LoadingComponent
}: SetupGuideDocsProps) {
  const iframeRef = useRef(null);
  const [blurredSBSDocs] = useState(false);

  const { eventTrack } = useAnalyticsTracker();

  const onMessageFromIframe = (e: DocWindowMessage) => {
    trackDocEvents(e);
  };

  const onIframeLoadError = () => {
    eventTrack({
      action: SetupGuideTracking.DOCS_ERRORED,
      properties: {
        setupGuideLink: docLink
      }
    });
  };

  const onIframeLoadSuccess = () => {
    eventTrack({
      action: SetupGuideTracking.DOCS_LOADED,
      properties: {
        setupGuideLink: docLink
      }
    });

    onLoadSuccess();
  };

  const {
    error,
    iframeSourceURL,
    isIframeLoading,
    onIframeLoadEvent,
    onIframeLoadErrorEvent,
    sendMessageToIframe
  } = useDocs({
    loadMessage: CommunicationMessageEvents.DASH_LOAD_DOCS,
    loadedMessage: CommunicationMessageEvents.DASH_LOAD_DOCS,
    iframeRef,
    docLink,
    areTabsRendered,
    queryParams,
    onMessage: onMessageFromIframe,
    onIframeLoadError,
    onIframeLoadSuccess
  });

  const dataIdGenerator = getDataIdGenerator(dataId, 'setup-guide-docs');

  const trackDocEvents = (e: DocWindowMessage) => {
    const { event, data } = e;
    let action;
    const properties = {
      ...data
    };

    switch (event) {
      case CommunicationMessageEvents.DOCS_LINK_CLICK:
        action = SetupGuideTracking.DOCS_LINK_CLICK;
        break;

      case CommunicationMessageEvents.DOCS_SCROLL_EVENT:
        action = SetupGuideTracking.DOCS_SCROLL;
        break;

      case CommunicationMessageEvents.DOCS_SCROLL_TOP_ClICK:
        action = SetupGuideTracking.DOCS_SCROLL_TOP_CLICK;
        break;

      case CommunicationMessageEvents.DOCS_SKIP_TO_SECTION_CLICK:
        action = SetupGuideTracking.DOCS_SKIP_TO_SECTION_CLICK;
        break;

      case CommunicationMessageEvents.DOCS_HEADER_CLICK:
        action = SetupGuideTracking.DOCS_HEADER_CLICK;
        break;

      case CommunicationMessageEvents.DOCS_TABS_LOADED:
        action = SetupGuideTracking.DOCS_TABS_LOADED;
        break;

      case CommunicationMessageEvents.DOCS_TABS_INTERACTION:
        action = SetupGuideTracking.DOCS_TABS_INTERACTION;
        break;

      default:
        action = null;
    }

    if (action && properties) {
      eventTrack({ action, properties });
    }
  };

  const onLoadSuccess = () => {
    // TODO: update this
  };

  return (
    <div data-id={`${dataId}-setup-guide-docs`} className={clsx('h-100', className)}>
      <DocsIframe
        dataId={dataIdGenerator('')}
        iFrameTitle='setup guide section'
        ref={iframeRef}
        className={clsx(styles.iframeContainer, blurredSBSDocs && styles.backdrop, iframeClassName)}
        error={error}
        docLink={docLink}
        iframeSourceURL={iframeSourceURL}
        isIframeLoading={isIframeLoading}
        isParentLoading={isParentLoading}
        LoadingComponent={LoadingComponent}
        onIframeLoadEvent={onIframeLoadEvent}
        onIframeLoadErrorEvent={onIframeLoadErrorEvent}
        sendMessageToIframe={sendMessageToIframe}
      />
    </div>
  );
}
